// @ts-strict-ignore
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {create, InstanceProps} from 'react-modal-promise';
import clsx from 'clsx';
import {sprig} from '@sprig-technologies/sprig-browser';

import {ModalReact, ModalType} from 'common/components/modal';
import {withStyles} from 'common/util/react';
import {useTranslation} from 'common/i18n/hooks';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {reporter} from 'common/report/error-reporting';
import {getProfilePath} from 'common/report/util/profile/utils';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey, ProjectFeatureFlag} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {IronContextRegistry} from 'common/report/context-registry';
import {Visibility} from 'common/components/base/stackable';
import {ButtonTheme} from 'common/components/button/constants';
import {TableReact} from 'common/components/table';
import {InputReact} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonReact} from 'common/components/button';
import {useDomEvent} from 'common/hooks/useDomEvent';
import {getLocation} from 'common/util/url';
import {ReplayBanner} from 'common/widgets/replay-banner';
import {getSessionRecordingBlockingInfo} from 'common/report/util/feature-sampling';
import {SwitchEl} from 'common/components/switch';
import {DropMenuReact} from 'common/components/drop-menu';
import {PropertiesMenuReact} from 'common/query-builder/query-block/properties-menu';
import {Directions} from 'common/components/drop-menu/types';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {renameEvent} from 'common/report/util/events';
import {Placement} from 'common/components/tooltip/types';
import {EndTableRowsScrollEvent} from 'common/components/table/types';

import {CopyToClipboardButtonReact} from '../copy-to-clipboard/copy-to-clipboard-button';
import {CopyType} from '../copy-to-clipboard/copy-to-clipboard-button/types';
import {DateRange} from '../date-range-picker/models/date-range';
import {getBannerTypeToDisplay, ReplayBannerType} from '../replay-banner/util';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import tableCss from './table.inline.styl';
import eventIndicatorCss from './event-indicator/index.module.styl?inline';
import {
  DeprecatedSortOptionTranslationLabels,
  EmbedErrorStateType,
  FilterEntry,
  RecordingEventMetadata,
  RecordingEvents,
  RecordingMetadata,
  RecordingPlayerSource,
  RecordingPlayerTrackingEvent,
  ReplayErrorStateType,
  SortOption,
} from './types';
import {RecordingPlayer, REPORT_EVENT_TIME_JUMP_OFFSET} from './player';
import {DummyPlaceHolderReplayPlaylistRow, ReplayLoadingPlaylistRowEl, ReplayPlaylistRowEl} from './playlist-row';
import {ReplayPlaylistRowEventMetadataEl} from './playlist-row-events';
import {
  filterPinnedReplayProperties,
  formatRecordingProgressTimeBar,
  getDisplayDate,
  getDisplayName,
  getDisplayTime,
  getPinnedReplayPropertyDisabledStatus,
  getPinnedReplayPropertyNames,
  isSortOptionAscending,
  PINNED_REPLAY_PROPERTIES,
  useReplayEvents,
  useReplayPageViewEvents,
} from './util';
import {ReplayPlaylistRowContextMenuReact} from './playlist-row-context-menu';
import {REPLAY_EVENT} from './query-utils';
import {PlaylistHeader} from './playlist-header';
import {AiSummaryPanel} from './ai-summary-panel';

const PERSISTENCE_KEY = `replay-modal-persistence-key`;

const SPRIG_SURVEY_EVENT = `SessionReplayModalClosed`;
const Sprig = sprig.configure({
  environmentId: `9sZai6GO-WSS`,
});

type RecordingPlaylistPlayerProps = {
  replays: RecordingMetadata[];
  replayDateRange?: DateRange;
  open: boolean;
  closeFn?: () => void;
  source: RecordingPlayerSource;
  loadingReplays?: boolean; // Used to indicate that the replays are still being queried outside the component
  enableUpdateURL?: boolean; // Used to enable URL updating feature
  startingTimestamp?: string; // Used to start the replay at a specific timestamp
  startingReplayId?: string; // Used to start the playlist at a replay
  initialEvents?: RecordingEvents[]; // Used to pass in events if they are already queried
  disableInitialEventsSearch?: boolean; // Used to avoid filling search when passing in initial events
  extraTrackingProps?: Record<string, any>;
  setDateRangeFn?: (dateRange: DateRange) => void;
  loadMoreReplaysFn?: () => void;
  isLoadingMoreReplays?: boolean; // Used to show spinner on table to represent we are loading more replays
  queryError?: string;
  replayCount?: number | null; // Used to display the total amount of recordings available
  onClose?: () => void; // Used whenever navigating away from the RecordingPlaylistPlayer
  eventFilterEntries?: FilterEntry[];
  setEventFilterEntries?: (entries: FilterEntry[]) => void;
  recordingPropertyFilters?: FilterEntry[];
  setRecordingPropertyFilters?: (entries: FilterEntry[]) => void;
} & Pick<IronContextRegistry, `localStorageManager`>;

type PersistedState = {
  seenReplayIds: string[];
  skipInactivity?: boolean;
};

function updateURL(distinctId: string, replayId: string | null, timestamp: number | null = null) {
  window.history.replaceState(null, null, getProfilePath(distinctId, null, replayId, timestamp));
}

export function RecordingPlaylistPlayer(props: RecordingPlaylistPlayerProps) {
  const startingReplayIdx = props.startingReplayId
    ? props.replays.findIndex((replay) => replay.replayId === props.startingReplayId)
    : 0;
  const [currentRecordingIdx, setCurrentRecordingIdx] = useState<number>(
    startingReplayIdx >= 0 ? startingReplayIdx : 0,
  );
  const [viewedRecordings, setViewedRecordings] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState<string>(``);
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(SortOption.MostRecent);
  const [visibleRecordingsCount, setVisibleRecordingsCount] = useState<number>(props.replays.length);
  const [aiSummaryPanelCollapsed, setAiSummaryPanelCollapsed] = useState<boolean>(true);
  const [sortedReplays, setSortedReplays] = useState<RecordingMetadata[]>([]);
  const [copyRecordingProgressTime, setCopyRecordingProgressTime] = useState<number>(0);
  const [currentRecordingEventSelectedTime, setCurrentRecordingEventSelectedTime] = useState<number>(null);
  const [currentRecordingPaused, setCurrentRecordingPaused] = useState<boolean>(false);
  const [isStitchingEnabled, setIsStitchingEnabled] = useState<boolean>(true);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState<boolean>(true);

  const [expandedRowsReplayIds, setExpandedRowsReplayIds] = useState<Set<string>>(new Set());
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [replayBannerType, setReplayBannerType] = useState<ReplayBannerType>(ReplayBannerType.NoBanner);
  const [isSkipInactivityEnabled, setIsSkipInactivityEnabled] = useState<boolean>(false);
  const [currentContextMenuRecordingIdx, setCurrentContextMenuRecordingIdx] = useState<number | null>(null);
  const [replayLoadedProps, setReplayLoadedProps] = useState<Obj>(null);
  const [hoveredEvent, setHoveredEvent] = useState<RecordingEventMetadata | null>(null);
  const [customizePropertiesMenuOpen, setCustomizePropertiesMenuOpen] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<Property[]>(PINNED_REPLAY_PROPERTIES);

  const playlistTableRef = useRef<HTMLDivElement>(null);
  const currentSelectedRowRef = useRef<HTMLDivElement>(null);
  const playlistModalRef = useRef<HTMLDivElement>(null);
  const currentContextMenuRowRef = useRef<HTMLDivElement>(null);
  const customizePropertiesButtonRef = useRef(null);
  const recordingProgressTime = useRef<number>(0);
  const lastReplayFetchedIdx = useRef<number>(0);

  const currentProject = useCurrentProject();
  const blockedReplayFrames = getSessionRecordingBlockingInfo(currentProject.get().value);
  const currentRecording = sortedReplays[currentRecordingIdx];
  const isAiSummaryEnabled = currentProject.get().value?.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingAiSummary);

  function getTrackingProps(replayToTrack?: RecordingMetadata) {
    replayToTrack = replayToTrack || currentRecording;
    const replayProps = replayToTrack
      ? {
          tracked_replay_duration: replayToTrack.duration,
          replay_sdk: replayToTrack.source,
          replay_id: replayToTrack.replayId,
          replay_distinct_id: replayToTrack.distinctId,
          replay_retention_period: replayToTrack.retentionPeriod,
          is_stitched: replayToTrack.isStitchedFromEvent,
        }
      : {};

    return {
      ...(props.extraTrackingProps || {}),
      banner_type: replayBannerType,
      blocked_replay_frames: blockedReplayFrames,
      is_fullscreen: isFullscreen,
      is_loading_replays: props.loadingReplays,
      is_server_side_stitching_enabled: true,
      is_skip_inactivity_enabled: isSkipInactivityEnabled,
      num_played: viewedRecordings.size,
      num_replays_loaded: props.replays.length,
      num_replays_stitched: props.replays.filter((r) => r.isStitchedFromEvent).length,
      selected_sort_option: selectedSortOption,
      tracked_replay_duration: currentRecording?.duration,
      replay_sdk: currentRecording?.source,
      source: props.source,
      ...replayProps,
      ...(replayLoadedProps ?? {}),
    };
  }

  useEffect(() => {
    mixpanel.track(RecordingPlayerTrackingEvent.Open, getTrackingProps());
    return () => {
      mixpanel.track(RecordingPlayerTrackingEvent.Close, getTrackingProps());
    };
  }, []);

  const userLocalStorageHelper: UserLocalStorageHelper<PersistedState> = new UserLocalStorageHelper({
    context: props.localStorageManager,
    namespace: LocalStorageKey.SeenReplayIds,
  });
  const {t, print} = useTranslation();
  const {
    recordingsEventMetadataLoading,
    hasLoadedAllEvents,
    getReplayEventsForId,
    hasReplayPageViewEventsFromEventsQuery,
  } = useReplayEvents({
    replays: props.replays,
    selectedReplay: sortedReplays[currentRecordingIdx],
    initialEvents: props.initialEvents || null,
    selectedProperties,
    paginationNumber: lastReplayFetchedIdx.current,
  });

  const {replayPageViewEventsLoaded, getReplayPageViewEventsForId, hasReplayPageViewEvents} = useReplayPageViewEvents(
    props.replays,
    currentProject.get().value,
  );

  useEffect(() => {
    const persistedState = getPersistedState();
    setViewedRecordings(persistedState.seenReplayIds ? new Set(persistedState.seenReplayIds) : new Set());
    setIsSkipInactivityEnabled(persistedState.skipInactivity ?? false);
    setTimeout(() => scrollCurrentRecordingToTop(), 200);

    return () => {
      Sprig(`identifyAndTrack`, {
        eventName: SPRIG_SURVEY_EVENT,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id.toString(),
        showSurveyCallback: () => Promise.resolve(true),
      });
    };
  }, []);

  useEffect(() => {
    clearPlayedReplay();
    lastReplayFetchedIdx.current = 0;
  }, [props.eventFilterEntries, props.recordingPropertyFilters]);

  function handleTableScrollEnd(ev: EndTableRowsScrollEvent) {
    if (
      ev.detail.reachedEnd &&
      !props.isLoadingMoreReplays &&
      props.replays.length > 0 &&
      !props.loadingReplays &&
      props.loadMoreReplaysFn
    ) {
      props.loadMoreReplaysFn();
      mixpanel.track(RecordingPlayerTrackingEvent.LoadMoreReplays, {
        ...getTrackingProps(),
        current_replay_count: props.replays.length,
      });
      // The prop.replays always same order so that allows index tracking.
      lastReplayFetchedIdx.current = props.replays.length;
    }
  }

  useDomEvent(document, `fullscreenchange`, () => onFullscreenChange());

  useEffect(() => {
    const replaysCopy = [...props.replays];
    const _replayBannerType = getBannerTypeToDisplay(
      blockedReplayFrames,
      props.replayDateRange?.getCalendarDatesAsOf(),
    );
    setReplayBannerType(_replayBannerType);
    const lastReplayId = sortedReplays[currentRecordingIdx]?.replayId ?? props.startingReplayId;
    const newSortedReplays = sortReplaysBySortOption(replaysCopy);
    const newStartingReplayIdx = lastReplayId
      ? newSortedReplays.findIndex((replay) => replay.replayId === lastReplayId)
      : 0;
    const newCurrentRecordingIdx = newStartingReplayIdx >= 0 ? newStartingReplayIdx : 0;
    setCurrentRecordingIdx(newCurrentRecordingIdx);
    setSortedReplays(newSortedReplays);
    if (newSortedReplays.length > 0) {
      if (!props.startingTimestamp && !props.disableInitialEventsSearch) {
        const targetEvent = props.initialEvents?.find((entry) =>
          entry.replayId.includes(newSortedReplays[newCurrentRecordingIdx].replayId),
        );
        if (
          targetEvent &&
          targetEvent.replayEvents[0]?.eventName !== REPLAY_EVENT &&
          targetEvent.replayEvents[0]?.eventName !== `$all_events`
        ) {
          const targetTime = targetEvent.replayEvents[0]?.eventTime || 0;
          const targetEventName = targetEvent.replayEvents[0]?.eventName || ``;
          setCurrentRecordingEventSelectedTime(Math.max(targetTime - REPORT_EVENT_TIME_JUMP_OFFSET, 0));
          setSearchFilter(renameEvent(targetEventName));
        }
      } else {
        setCurrentRecordingEventSelectedTime(getTimestampFromReplaySeconds(props.startingTimestamp, newSortedReplays));
      }
    }
  }, [props.replays]);

  function getTimestampFromReplaySeconds(secondsInString: string, newSortedReplays: RecordingMetadata[]) {
    let secondsIn = 0;
    try {
      secondsIn = parseFloat(secondsInString);
    } catch {
      secondsIn = 0;
    }

    if (newSortedReplays.length > 0 && secondsIn) {
      return newSortedReplays[currentRecordingIdx].time + secondsIn;
    }
    return null;
  }

  function hasBeenPlayed(replayId: string): boolean {
    return viewedRecordings.has(replayId);
  }

  function playReplayAtIndex(replayIdx: number, selectedTime?: number) {
    setCurrentRecordingIdx(replayIdx);
    setCurrentRecordingEventSelectedTime(selectedTime ?? null);

    // reset the loaded props
    setReplayLoadedProps(null);
  }

  function updateSearchFilter(newValue: string) {
    setSearchFilter(newValue);
    if (!newValue) {
      setExpandedRowsReplayIds(new Set(currentRecording?.replayId ? [currentRecording.replayId] : []));
    } else {
      const allReplayIds = sortedReplays.map((replay) => replay.replayId);
      setExpandedRowsReplayIds(new Set(allReplayIds));
    }
  }

  const updateSortOption = useCallback(
    (newValue: SortOption) => {
      setSelectedSortOption(newValue);
      if (sortedReplays.length > 0) {
        const newSortedReplays = sortReplaysBySortOption(sortedReplays, newValue);
        const newIndexOfCurrentReplay = newSortedReplays.findIndex(
          (replay) => replay.replayId === sortedReplays[currentRecordingIdx]?.replayId,
        );
        setSortedReplays(newSortedReplays);
        playReplayAtIndex(newIndexOfCurrentReplay);
      }
      mixpanel.track(RecordingPlayerTrackingEvent.Sort, {...getTrackingProps(), selected_sort_option: newValue});
    },
    [sortedReplays],
  );

  function sortReplaysBySortOption(currentReplays: RecordingMetadata[], currentSortOption = selectedSortOption) {
    const newSortedReplays = [...currentReplays];
    switch (currentSortOption) {
      case SortOption.MostRecent:
        return newSortedReplays.sort((a, b) => b.time - a.time);
      case SortOption.LeastRecent:
        return newSortedReplays.sort((a, b) => a.time - b.time);
      case SortOption.LongestDuration:
        return newSortedReplays.sort((a, b) => b.duration - a.duration);
      case SortOption.ShortestDuration:
        return newSortedReplays.sort((a, b) => a.duration - b.duration);
      case SortOption.MostEvents:
        return newSortedReplays.sort(
          (a, b) => getReplayEventsForId(b.replayId).length - getReplayEventsForId(a.replayId).length,
        );
      case SortOption.LeastEvents:
        return newSortedReplays.sort(
          (a, b) => getReplayEventsForId(a.replayId).length - getReplayEventsForId(b.replayId).length,
        );
    }
  }

  function getPersistedState(): PersistedState {
    try {
      const persistedState = userLocalStorageHelper.getItem({
        key: PERSISTENCE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      });
      return persistedState || {seenReplayIds: []};
    } catch (err) {
      reporter.warn(err, `Unable to retrieve ${PERSISTENCE_KEY} value from localStorage.`);
      return {seenReplayIds: []};
    }
  }

  function setPersistedState(state: PersistedState): void {
    try {
      userLocalStorageHelper.setItem({
        key: PERSISTENCE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
        value: state,
      });
    } catch (err) {
      reporter.warn(err, `Unable to persist ${PERSISTENCE_KEY} to localStorage.`);
    }
  }

  function persistPlayedReplay(replayId: string): void {
    const currentState = getPersistedState();
    const updatedValue = [...currentState.seenReplayIds, replayId];
    setPersistedState({...currentState, seenReplayIds: updatedValue});
    setViewedRecordings(new Set(updatedValue));
  }

  function clearPlayedReplay(): void {
    const currentState = getPersistedState();
    setPersistedState({...currentState, seenReplayIds: []});
  }

  function saveSkipInactivity(skipInactivity: boolean): void {
    const currentState = getPersistedState();
    setPersistedState({...currentState, skipInactivity});
  }

  function findReplayIdx(replayId: string) {
    return sortedReplays.findIndex((replay) => replay.replayId === replayId);
  }

  function playNextRecording(replayId: string) {
    const replayIdx = findReplayIdx(replayId);
    const nextRecordingIdx = replayIdx + 1;

    if (!searchFilter && nextRecordingIdx < sortedReplays.length && nextRecordingIdx >= 0) {
      updateExpandedRows(sortedReplays[nextRecordingIdx].replayId);

      playReplayAtIndex(nextRecordingIdx);
      setTimeout(() => scrollCurrentRecordingToTop(), 200);
    }
  }

  function updateExpandedRows(newExpandedReplayId: string, collapseCurrent = true) {
    const newExpandedRowsReplayIds = new Set(expandedRowsReplayIds);
    if (collapseCurrent && newExpandedRowsReplayIds.has(sortedReplays[currentRecordingIdx]?.replayId)) {
      newExpandedRowsReplayIds.delete(sortedReplays[currentRecordingIdx]?.replayId);
    }
    newExpandedRowsReplayIds.add(newExpandedReplayId);
    setExpandedRowsReplayIds(newExpandedRowsReplayIds);
  }

  function copyReplayLink(distinctId: string, replayId: string, includeTime = false) {
    const secondsIn = copyRecordingProgressTime - sortedReplays[currentRecordingIdx]?.time;
    return (
      getLocation().origin +
      getProfilePath(distinctId, null, replayId, includeTime ? Math.max(secondsIn, 0) : undefined)
    );
  }

  function jumpToEventTime(time: number, replayId: string) {
    if (replayId === sortedReplays[currentRecordingIdx]?.replayId) {
      setCurrentRecordingEventSelectedTime(time);
    } else {
      const replayIdx = findReplayIdx(replayId);
      playReplayAtIndex(replayIdx, time);
    }
    // Clear the selected time after 1 second to ensure repeated jumping to the same event is possible
    setTimeout(() => setCurrentRecordingEventSelectedTime(null), 1000);
  }

  function onToggleExpand(replayId: string) {
    const newExpandedRowsReplayIds = new Set(expandedRowsReplayIds);
    if (newExpandedRowsReplayIds.has(replayId)) {
      newExpandedRowsReplayIds.delete(replayId);
    } else {
      newExpandedRowsReplayIds.add(replayId);
    }
    setExpandedRowsReplayIds(newExpandedRowsReplayIds);
  }

  function onFullscreenChange() {
    if (document.fullscreenElement) {
      setIsFullscreen(true);
    } else {
      setIsFullscreen(false);
    }
  }

  function onToggleFullscreen() {
    if (playlistModalRef.current) {
      if (!document.fullscreenElement && !isFullscreen) {
        playlistModalRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  }

  function scrollCurrentRecordingToTop() {
    if (playlistTableRef.current && currentSelectedRowRef.current) {
      const scrollDistance =
        currentSelectedRowRef.current.getBoundingClientRect().top -
        playlistTableRef.current.getBoundingClientRect().top;
      playlistTableRef.current.scrollBy({top: scrollDistance, behavior: `smooth`});
    }
  }

  function getSortFromOption(option: SortOption) {
    switch (option) {
      case SortOption.MostRecent:
        return {field: `time`, order: -1};
      case SortOption.LeastRecent:
        return {field: `time`, order: 1};
      case SortOption.LongestDuration:
        return {field: `duration`, order: -1};
      case SortOption.ShortestDuration:
        return {field: `duration`, order: 1};
      case SortOption.MostEvents:
        return {field: `eventCount`, order: -1};
      case SortOption.LeastEvents:
        return {field: `eventCount`, order: 1};
      default:
        return {field: `time`, order: -1};
    }
  }

  function getErrorState(): ReplayErrorStateType | EmbedErrorStateType | null {
    if (!currentProject.get().value?.hasIntegratedSessionReplay) {
      return ReplayErrorStateType.NotIntegrated;
    }

    if (props.queryError) {
      return ReplayErrorStateType.LoadError;
    }

    let filteredReplays = sortedReplays;
    if (!isStitchingEnabled) {
      filteredReplays = filteredReplays.filter((replay) => !replay.isStitchedFromEvent);
    }

    if (!props.loadingReplays && !filteredReplays.length) {
      if (props.source === RecordingPlayerSource.UsersProfileLink) {
        return EmbedErrorStateType.NoReplayFound;
      }
      return ReplayErrorStateType.NoReplaysAvailable;
    }
    return null;
  }

  function getPlaylist() {
    const sortInfo = getSortFromOption(selectedSortOption);
    let filteredReplays = sortedReplays;
    if (!isStitchingEnabled) {
      filteredReplays = filteredReplays.filter((replay) => !replay.isStitchedFromEvent);
    }

    return (
      <div className={`${styles.replaysListItemContainer}`} role={`rowgroup`} ref={playlistTableRef}>
        <TableReact
          onTableRowsScrollEnd={handleTableScrollEnd}
          emptyMessage={t(`session-replay.no-replays-found`)}
          loading={props.loadingReplays}
          loadingMoreItems={props.isLoadingMoreReplays}
          customLoadingRowRenderer={() => ReplayLoadingPlaylistRowEl({sel: `.${styles.replayPlaylistRowEl}`})}
          defaultSortField={sortInfo.field}
          defaultSortOrder={sortInfo.order}
          searchFilter={searchFilter}
          className={styles.replaysListTable}
          rows={filteredReplays.map((replay) => ({
            ...replay,
            isExpanded: expandedRowsReplayIds.has(replay.replayId),
            eventCount: getReplayEventsForId(replay.replayId).length,
            replayEvents: getReplayEventsForId(replay.replayId),
            isSticky: replay.replayId === sortedReplays[currentRecordingIdx]?.replayId,
          }))}
          columns={[
            {
              field: `name`,
              flexGrow: `1`,
              cellRenderer: (replay: RecordingMetadata) =>
                ReplayPlaylistRowEl({
                  sel: `.${styles.replayPlaylistRowEl}`,
                  sortedReplays,
                  currentRecordingIdx,
                  currentlyPlaying:
                    replay.replayId === sortedReplays[currentRecordingIdx]?.replayId && !currentRecordingPaused,
                  hasBeenPlayed: hasBeenPlayed(replay.replayId),
                  replay,
                  replayEvents: getReplayEventsForId(replay.replayId) || [],
                  replayPageViewEventsLoaded,
                  replayPageViewEvents: getReplayPageViewEventsForId(replay.replayId) || [],
                  displayName: getDisplayName(replay.email, replay.name, replay.distinctId),
                  displayDate: print(getDisplayDate(replay.time, currentProject.get().value?.timezone)),
                  displayTime: print(getDisplayTime(replay.time, currentProject.get().value?.timezone)),
                  searchFilter,
                  isExpanded: expandedRowsReplayIds.has(replay.replayId),
                  onToggleExpand,
                  onTogglePlay: (replayId) => {
                    if (replayId === sortedReplays[currentRecordingIdx]?.replayId) {
                      setCurrentRecordingPaused(!currentRecordingPaused);
                    }
                  },
                  onHover: (hovered, currentTarget) => {
                    if (hovered) {
                      const currentIdx = findReplayIdx(replay.replayId);
                      setCurrentContextMenuRecordingIdx(currentIdx);
                      currentContextMenuRowRef.current = currentTarget;
                    } else {
                      setCurrentContextMenuRecordingIdx(null);
                    }
                  },
                  ref: replay.replayId === sortedReplays[currentRecordingIdx]?.replayId ? currentSelectedRowRef : null,
                  isStitchingEnabled,
                }),
              labelTransform: (row, _field) => {
                const eventNames = new Set(row.replayEvents.map((event: RecordingEventMetadata) => event.eventName));
                const eventNamesString = Array.from(eventNames).join(` `);
                const propertyNamesString = row.replayEvents
                  .map((event: RecordingEventMetadata) => {
                    return Array.from(event.propertyMapping.entries())
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(` `);
                  })
                  .join(` `);

                return `${getDisplayName(row.email, row.name, row.distinctId)} ${print(
                  getDisplayDate(row.time, currentProject.get().value?.timezone),
                )} ${print(
                  getDisplayTime(row.time, currentProject.get().value?.timezone),
                )} ${row.distinctId} ${eventNamesString} ${propertyNamesString}`;
              },
              isSearchable: true,
            },
          ]}
          rowDetailsRenderer={(replay: RecordingMetadata) => {
            return ReplayPlaylistRowEventMetadataEl({
              sel: `.${styles.replayPlaylistRowEventMetadataEl}`,
              loadingEventMetadata: recordingsEventMetadataLoading && !getReplayEventsForId(replay.replayId)?.length,
              replay,
              replayEvents: getReplayEventsForId(replay.replayId),
              jumpToEvent: jumpToEventTime,
              currentRecordingProgressTime:
                replay.replayId === sortedReplays[currentRecordingIdx]?.replayId ? recordingProgressTime.current : null,
              searchFilter,
              isStitchingEnabled,
              currentHoveredEvent: hoveredEvent,
              setCurrentHoveredEvent: setHoveredEvent,
              selectedPropertyCount: filterPinnedReplayProperties(selectedProperties).length,
            });
          }}
          windowScroll={false}
          hideTitles
          onRowClick={({detail}) => {
            const replayIdx = findReplayIdx(detail.row.replayId as string);

            updateExpandedRows(detail.row.replayId);
            playReplayAtIndex(replayIdx);
            setTimeout(() => scrollCurrentRecordingToTop(), 200);
          }}
          onNumResultsChange={({detail}) => {
            setVisibleRecordingsCount(detail.numResults);
          }}
          disableExpandableRows
          disableExpandableRowsIndent
          disableRowBackgroundHover
          stickyRowMode
          style-override={tableCss + eventIndicatorCss}
        />
      </div>
    );
  }

  let formattedRecordingProgress = `00:00`;
  if (sortedReplays.length > 0) {
    const formatRecordingProgressTimeBarResult = formatRecordingProgressTimeBar(
      (copyRecordingProgressTime - currentRecording.time) * 1000,
      currentRecording.duration,
    );
    formattedRecordingProgress = formatRecordingProgressTimeBarResult.formattedProgress;
  }

  let currentContextMenuRecording = null;
  if (currentContextMenuRecordingIdx !== null && sortedReplays.length > currentContextMenuRecordingIdx) {
    currentContextMenuRecording = sortedReplays[currentContextMenuRecordingIdx];
  }

  return (
    <div className={styles.recordingContainer}>
      <div
        className={`${styles.recordingModal} ${
          props.source === RecordingPlayerSource.ReplayHome ? styles.noBorderRadius : ``
        }`}
        ref={playlistModalRef}
      >
        <div className={styles.replaysList}>
          <PlaylistHeader
            source={props.source}
            loadingReplays={props.loadingReplays}
            setDateRangeFn={props.setDateRangeFn}
            replayDateRange={props.replayDateRange}
            visibleRecordingsCount={visibleRecordingsCount}
            selectedSortOption={selectedSortOption}
            updateSortOptionFn={updateSortOption}
            replayCount={props.replayCount}
            eventFilterEntries={props.eventFilterEntries}
            setEventFilterEntries={props.setEventFilterEntries}
            recordingPropertyFilters={props.recordingPropertyFilters}
            setRecordingPropertyFilters={props.setRecordingPropertyFilters}
          />
          <div className={styles.replaysListControlsTop}>
            <InputReact
              icon={`search`}
              placeholder={t(`session-replay.search-placeholder`)}
              size={InputSize.Medium}
              value={searchFilter}
              onInput={(ev) => updateSearchFilter(ev.target.value)}
              showClear
              onClear={() => updateSearchFilter(``)}
            />
            {props.source !== RecordingPlayerSource.ReplayHome && (
              <SelectReact
                theme={SelectTheme.Secondary}
                iconOnly
                icon={{
                  leftIcon: isSortOptionAscending(selectedSortOption) ? `sort-value-asc` : `sort-value-desc`,
                }}
                menuWidth={150}
                sections={[
                  {
                    items: Object.keys(SortOption).map((option) => ({
                      value: option,
                      label: print(t(DeprecatedSortOptionTranslationLabels[option])),
                    })),
                  },
                ]}
                options={Object.values(SortOption)}
                onChange={(ev) => {
                  updateSortOption(ev.detail.item.value as SortOption);
                }}
                selectedOption={selectedSortOption}
                tooltip={{text: t(`session-replay.sort-tooltip`)}}
              />
            )}
            <ButtonReact
              theme={ButtonTheme.Secondary}
              icon='column-select'
              onClick={() => setCustomizePropertiesMenuOpen(!customizePropertiesMenuOpen)}
              tooltip={{text: t(`session-replay.customize-properties`)}}
              ref={customizePropertiesButtonRef}
            />
            <DropMenuReact
              open={customizePropertiesMenuOpen}
              onChange={(ev) => {
                if (ev.detail.state === Visibility.Closed) {
                  setCustomizePropertiesMenuOpen(false);
                }
              }}
              positionParent={customizePropertiesButtonRef.current}
              direction={Directions.DownRight}
            >
              <PropertiesMenuReact
                open={customizePropertiesMenuOpen}
                selected={selectedProperties}
                multiple
                hideRecentlyViewed
                hideEvents={false}
                hideExperiments
                excludeDimensionProperties
                maxSelection={6}
                excludeClearProperties={getPinnedReplayPropertyNames()}
                disabledItems={getPinnedReplayPropertyDisabledStatus()}
                minSelection={PINNED_REPLAY_PROPERTIES.length}
                onSelect={(ev) => {
                  setSelectedProperties(ev.item as Property[]);
                  setCustomizePropertiesMenuOpen(false);
                }}
              />
            </DropMenuReact>
          </div>
          <div
            className={clsx({
              [styles.replayBannerContainerDismissible]: replayBannerType === ReplayBannerType.Dismissible,
              [styles.replayBannerContainerNonDismissible]: replayBannerType === ReplayBannerType.NonDismissible,
            })}
          >
            <div
              className={clsx({
                [styles.replayBannerNonDismissiblePosition]: replayBannerType === ReplayBannerType.NonDismissible,
              })}
            >
              <ReplayBanner bannerType={replayBannerType} />
            </div>
            {replayBannerType === ReplayBannerType.NonDismissible && (
              <div className={styles.blurredRow}>
                <DummyPlaceHolderReplayPlaylistRow />
              </div>
            )}
          </div>
          {getPlaylist()}
          <div className={styles.replaysListControlsBottom}>
            <div className={styles.replaysListSettings}>
              <SelectReact
                icon={{leftIcon: `settings`}}
                theme={SelectTheme.Tertiary}
                iconOnly
                menuHeight={0}
                disableCloseOnSelect
                menuWidth='null'
                sections={[
                  {
                    items: [
                      {
                        label: t(`session-replay.enable-stitching`),
                        shouldClose: false,
                        actionsRenderer: () =>
                          SwitchEl({
                            attrs: {
                              on: isStitchingEnabled,
                            },
                            on: {
                              change: (ev) => {
                                const isEnabled = ev.detail.on;
                                setIsStitchingEnabled(isEnabled);

                                mixpanel.track(RecordingPlayerTrackingEvent.ToggleStitching, {
                                  ...getTrackingProps(),
                                  is_stitching_enabled: isEnabled,
                                });

                                if (!isEnabled && currentRecording?.isStitchedFromEvent) {
                                  const nextObservedRecording = sortedReplays.find(
                                    (replay) => !replay.isStitchedFromEvent,
                                  );
                                  if (nextObservedRecording) {
                                    playNextRecording(nextObservedRecording.replayId);
                                  } else {
                                    setCurrentRecordingPaused(true);
                                  }
                                }
                              },
                            },
                          }),
                        tooltip: {
                          'max-width': `256px`,
                          text: t(`session-replay.enable-stitching-tooltip`, {
                            docsLink: `https://docs.mixpanel.com/docs/session-replay/implement-session-replay/session-replay-web#server-side-stitching`,
                          }),
                          placement: Placement.Top,
                        },
                      },
                      {
                        label: t(`session-replay.auto-play`),
                        shouldClose: false,
                        actionsRenderer: () =>
                          SwitchEl({
                            attrs: {
                              on: isAutoPlayEnabled,
                            },
                            on: {
                              change: (ev) => {
                                const isEnabled = ev.detail.on;
                                setIsAutoPlayEnabled(isEnabled);

                                mixpanel.track(RecordingPlayerTrackingEvent.ToggleAutoPlay, {
                                  ...getTrackingProps(),
                                  is_auto_play_enabled: isEnabled,
                                });
                              },
                            },
                          }),
                      },
                    ],
                  },
                ]}
              />
            </div>
          </div>
        </div>
        <div className={styles.recordingPlayerContainer}>
          <div className={styles.recordingPlayer}>
            <RecordingPlayer
              replay={currentRecording}
              replayEvents={getReplayEventsForId(currentRecording?.replayId)}
              isLoading={props.loadingReplays}
              onStartReplay={(replayId) => {
                const replayIdx = findReplayIdx(replayId);
                updateExpandedRows(replayId, false);
                if (props.enableUpdateURL) {
                  updateURL(sortedReplays[replayIdx].distinctId, replayId);
                }
                mixpanel.track(RecordingPlayerTrackingEvent.Watch, getTrackingProps(sortedReplays[replayIdx]));
              }}
              onFinishReplay={(replayId) => {
                persistPlayedReplay(replayId);
                if (isAutoPlayEnabled) {
                  playNextRecording(replayId);
                }
                mixpanel.track(
                  RecordingPlayerTrackingEvent.Finish,
                  getTrackingProps(sortedReplays[findReplayIdx(replayId)]),
                );
              }}
              onHalfwayThrough={(replayId) => {
                if (!viewedRecordings.has(replayId)) {
                  persistPlayedReplay(replayId);
                }
              }}
              onToggleSkipInactivity={(on) => {
                setIsSkipInactivityEnabled(on);
                saveSkipInactivity(on);
                mixpanel.track(RecordingPlayerTrackingEvent.ToggleSkipInactivity, {
                  ...getTrackingProps(),
                  is_skip_inactivity_enabled: on,
                });
              }}
              onToggleCurrentRecordingPaused={(paused) => {
                setCurrentRecordingPaused(paused);
              }}
              onTrackReplayLoaded={(loadedProps) => {
                mixpanel.track(RecordingPlayerTrackingEvent.ReplayLoaded, {
                  ...getTrackingProps(),
                  ...loadedProps,
                });
                setReplayLoadedProps(loadedProps);
              }}
              isSkipInactivityEnabled={isSkipInactivityEnabled}
              replayPageViewEventsLoaded={replayPageViewEventsLoaded}
              replayPageViewEvents={getReplayPageViewEventsForId(currentRecording?.replayId) || []}
              hasReplayPageViewEvents={hasReplayPageViewEvents() || hasReplayPageViewEventsFromEventsQuery()}
              updateCurrentReplayEvent={(time) => {
                recordingProgressTime.current = time;
              }}
              currentRecordingEventSelectedTime={currentRecordingEventSelectedTime}
              currentRecordingPaused={currentRecordingPaused}
              source={props.source}
              searchFilter={searchFilter}
              extraPlayerControls={
                <>
                  {isAiSummaryEnabled && (
                    <ButtonReact
                      icon='spark'
                      theme={ButtonTheme.Secondary}
                      onClick={() => {
                        const newState = !aiSummaryPanelCollapsed;
                        setAiSummaryPanelCollapsed(newState);
                        mixpanel.track(RecordingPlayerTrackingEvent.AiSummaryToggle, {
                          ...getTrackingProps(),
                          is_ai_summary_expanded: !newState,
                        });
                      }}
                      tooltip={{text: t(`session-replay.ai-summary-toggle`)}}
                      pressed={!aiSummaryPanelCollapsed}
                    >
                      {t(`session-replay.ai-summary-button`)}
                    </ButtonReact>
                  )}
                  <ButtonReact
                    className={styles.expandButton}
                    icon={isFullscreen ? `collapse-diagonal` : `expand`}
                    onClick={() => onToggleFullscreen()}
                    tooltip={{text: t(`session-replay.toggle-fullscreen`)}}
                  />
                  {sortedReplays.length !== 0 && (
                    <CopyToClipboardButtonReact
                      className={styles.copyButton}
                      copyType={CopyType.Link}
                      onCopy={(ev) => {
                        mixpanel.track(RecordingPlayerTrackingEvent.CopyLink, {
                          ...getTrackingProps(),
                          copied_url: ev.detail.value,
                        });
                      }}
                      options={[
                        {
                          value: copyReplayLink(currentRecording.distinctId, currentRecording.replayId),
                          label: t(`session-replay.copy-link`),
                        },
                        {
                          value: copyReplayLink(currentRecording.distinctId, currentRecording.replayId, true),
                          label: t(`session-replay.copy-link-position`, {position: formattedRecordingProgress}),
                        },
                      ]}
                      theme={ButtonTheme.Tertiary}
                      onClick={() => {
                        setCopyRecordingProgressTime(recordingProgressTime.current);
                      }}
                    />
                  )}
                </>
              }
              isStitchingEnabled={isStitchingEnabled}
              errorState={getErrorState()}
              queryError={props.queryError}
              currentHoveredEvent={hoveredEvent}
              setCurrentHoveredEvent={setHoveredEvent}
              onClose={props.onClose}
              closeModalFn={props.closeFn}
            />
          </div>
        </div>
        {isAiSummaryEnabled && !aiSummaryPanelCollapsed && (
          <AiSummaryPanel
            replay={currentRecording}
            isEventsLoading={
              props.loadingReplays || !hasLoadedAllEvents(currentRecording?.replayId) || !replayPageViewEventsLoaded
            }
            replayEvents={
              hasLoadedAllEvents(currentRecording?.replayId) ? getReplayEventsForId(currentRecording?.replayId) : []
            }
            replayPageViewEvents={getReplayPageViewEventsForId(currentRecording?.replayId) || null}
            onKeyActionTimestampClick={(action) => {
              if (action && currentRecording) {
                jumpToEventTime(action.timestamp / 1000, currentRecording.replayId);
                mixpanel.track(RecordingPlayerTrackingEvent.AiSummaryTimestampClick, {
                  ...getTrackingProps(),
                  timestamp_ms: action.timestamp,
                  action_description: action.description,
                });
              }
            }}
            onClose={() => {
              setAiSummaryPanelCollapsed(true);
              mixpanel.track(RecordingPlayerTrackingEvent.AiSummaryToggle, {
                ...getTrackingProps(),
                is_ai_summary_expanded: false,
              });
            }}
          />
        )}
      </div>
      {currentContextMenuRecording !== null && !customizePropertiesMenuOpen && (
        <ReplayPlaylistRowContextMenuReact
          contextPanelOpen={currentContextMenuRecording !== null}
          replay={currentContextMenuRecording}
          replayPageViewEvents={getReplayPageViewEventsForId(currentContextMenuRecording.replayId) || []}
          replayPageViewEventsLoaded={replayPageViewEventsLoaded}
          displayName={getDisplayName(
            currentContextMenuRecording.email,
            currentContextMenuRecording.name,
            currentContextMenuRecording.distinctId,
          )}
          sortedReplays={sortedReplays}
          currentRecordingIdx={currentRecordingIdx}
          positionParent={currentContextMenuRowRef?.current}
          onContextMenuHover={(currentIdx) => {
            setCurrentContextMenuRecordingIdx(currentIdx);
          }}
        />
      )}
    </div>
  );
}

export function RecordingPlaylistPlayerModal(props: RecordingPlaylistPlayerProps) {
  const modalRef = useRef<any>(null);

  const handleClose = useCallback(() => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  }, []);

  return (
    <ModalReact
      modalType={ModalType.Modal}
      hideCloseButton
      open={props.open}
      growToContent
      closeable
      elref='recordingPlayerModal'
      ref={modalRef}
      onChange={({detail}) => {
        if (detail.state === Visibility.Closed) {
          if (props.replays.length > 0 && props.enableUpdateURL) {
            const urlHash = window.location.hash;
            if (urlHash) {
              const params = new URLSearchParams(urlHash.substring(1));
              updateURL(params.get(`distinct_id`), null);
            }
          }
          props.onClose();
        } else if (detail.state === Visibility.Open && props.replays.length > 0 && props.enableUpdateURL) {
          const replayMetadata = props.replays.find((replay) => replay.replayId === props.startingReplayId);
          if (replayMetadata) {
            updateURL(replayMetadata.distinctId, replayMetadata.replayId);
          }
        }
      }}
    >
      <div className={styles.recordingContainerModal}>
        <RecordingPlaylistPlayer {...props} closeFn={handleClose} />
      </div>
    </ModalReact>
  );
}

export const RecordingPlaylistPlayerStyled = withStyles(RecordingPlaylistPlayer, css);
export const RecordingPlaylistPlayerModalStyled = withStyles(RecordingPlaylistPlayerModal, css);

export type RecordingPlayerModalTypes = {
  replays: RecordingMetadata[];
  replayDateRange?: DateRange;
  source: RecordingPlayerSource;
  loadingReplays?: boolean;
  emptyReplays?: boolean;
  enableUpdateURL?: boolean;
  startingTimestamp?: string;
  startingReplayId?: string;
  initialEvents?: RecordingEvents[];
  disableInitialEventsSearch?: boolean;
  extraTrackingProps?: Record<string, any>;
};
type RecordingPlayerModalProps = RecordingPlayerModalTypes &
  InstanceProps<unknown> &
  Pick<IronContextRegistry, `localStorageManager`>;

const RecordingPlayerModalWrapper: React.FC<RecordingPlayerModalProps> = (props: RecordingPlayerModalProps) => {
  return (
    <RecordingPlaylistPlayerModalStyled
      replays={props.replays}
      replayDateRange={props.replayDateRange}
      open={props.isOpen}
      onClose={props.onReject}
      source={props.source}
      loadingReplays={props.loadingReplays || false}
      enableUpdateURL={props.enableUpdateURL || false}
      startingTimestamp={props.startingTimestamp || null}
      startingReplayId={props.startingReplayId || null}
      initialEvents={props.initialEvents || []}
      localStorageManager={props.localStorageManager}
    />
  );
};

// Wrapper where you can pass in a promise to resolve the replays
type RecordingPlayerModalAsyncProps = Omit<
  RecordingPlayerModalProps,
  `replays` | `loadingReplays` | `initialEvents`
> & {
  eventsAndReplaysPromise: Promise<{replays: RecordingMetadata[]; events: RecordingEvents[]}>;
};
const RecordingPlayerModalAsyncWrapper: React.FC<RecordingPlayerModalAsyncProps> = (
  props: RecordingPlayerModalAsyncProps,
) => {
  const [replays, setReplays] = useState<RecordingMetadata[]>([]);
  const [loadingReplays, setLoadingReplays] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [initialEvents, setInitialEvents] = useState<RecordingEvents[]>([]);

  useEffect(() => {
    const startedAt = new Date().getTime();
    props.eventsAndReplaysPromise
      .then((loadedEventsAndReplays) => {
        setReplays(loadedEventsAndReplays.replays);
        setInitialEvents(loadedEventsAndReplays.events);
        setLoadingReplays(false);
        mixpanel.track(RecordingPlayerTrackingEvent.ListLoaded, {
          ...props.extraTrackingProps,
          source: props.source,
          is_server_side_stitching_enabled: true,
          num_replays_loaded: loadedEventsAndReplays.replays.length,
          'Load time (ms)': new Date().getTime() - startedAt,
        });
      })
      .catch((err) => {
        reporter.error(err);
        setLoadingReplays(false);
        setError(err);
        mixpanel.track(RecordingPlayerTrackingEvent.ListLoadFailed, {
          ...props.extraTrackingProps,
          source: props.source,
          is_server_side_stitching_enabled: true,
          error: err,
          'Load time (ms)': new Date().getTime() - startedAt,
        });
      });
  }, []);

  return (
    <RecordingPlaylistPlayerModalStyled
      replays={replays}
      replayDateRange={props.replayDateRange}
      queryError={error?.toString()}
      open={props.isOpen}
      onClose={props.onReject}
      source={props.source}
      loadingReplays={loadingReplays}
      enableUpdateURL={props.enableUpdateURL || false}
      startingTimestamp={props.startingTimestamp || null}
      startingReplayId={props.startingReplayId || null}
      initialEvents={initialEvents}
      disableInitialEventsSearch={props.disableInitialEventsSearch || false}
      localStorageManager={props.localStorageManager}
      extraTrackingProps={props.extraTrackingProps}
    />
  );
};

export const RecordingPlayerModal = create(RecordingPlayerModalWrapper);
export const RecordingPlayerModalAsync = create(RecordingPlayerModalAsyncWrapper);
